import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event.
   * Faithfull change: keep the gallery in its original order and move to the
   * selected variant's image, instead of rebuilding the gallery with that image first.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (!this.#goToMedia(newMediaGallery)) this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Moves the current gallery to the media shown first in the newly rendered gallery.
   * Returns false when the galleries hold different media, so the caller can fall back to replacing.
   *
   * @param {Element} newMediaGallery - The gallery rendered for the selected variant.
   * @returns {boolean}
   */
  #goToMedia(newMediaGallery) {
    const mediaIds = (/** @type {ParentNode} */ root) =>
      Array.from(root.querySelectorAll('slideshow-slide .product-media[data-media-id]'), (el) =>
        el.getAttribute('data-media-id')
      );

    const currentIds = mediaIds(this);
    const newIds = mediaIds(newMediaGallery);
    if (!currentIds.length || currentIds.length !== newIds.length) return false;
    if ([...currentIds].sort().join() !== [...newIds].sort().join()) return false;

    const targetId = newIds[0];
    const slides = this.slideshow?.slides;
    const index = slides?.findIndex(
      (slide) => slide.querySelector('.product-media')?.getAttribute('data-media-id') === targetId
    );
    if (index != null && index > -1) this.slideshow?.select(index);

    const gridItem = this.querySelector(`.media-gallery__grid .product-media[data-media-id="${targetId}"]`);
    const gridIsVisible = gridItem instanceof HTMLElement && gridItem.offsetParent !== null;
    if (gridIsVisible) gridItem.scrollIntoView({ behavior: 'smooth', block: 'center' });

    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
